import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { SelectTextField, SelectTextFieldProps } from '@lumapps/combobox/components/SelectTextField';
import type { Attributes as DataAttributes } from '@lumapps/data-attributes';
import { mdiMapClockOutline } from '@lumapps/lumx/icons';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { getFormattedTimezone, TimeZoneFormatOptions } from '@lumapps/utils/timezones/getFormattedTimezone';
import { getTimeZonesDetails } from '@lumapps/utils/timezones/getTimeZonesDetails';
import type { TimeZone } from '@lumapps/utils/types/Time';

type InheritedSelectTextFieldProps = Pick<
    SelectTextFieldProps,
    'label' | 'helper' | 'error' | 'hasError' | 'isDisabled' | 'isRequired'
>;

export interface TimeZonePickerFieldProps extends TimeZoneFormatOptions, InheritedSelectTextFieldProps {
    /** Icon override (set to `null` to remove the icon) */
    icon?: SelectTextFieldProps['icon'] | null;
    /** Forwarded class name */
    className?: string;
    /** Additional props to be passed into the list options */
    optionProps?: Partial<DataAttributes>;
    /** Callback to be executed once the timezone is selected */
    onTimeZoneChanged: (timeZone: TimeZone) => void;
    /** currently selected time zone */
    selectedTimeZone?: string;
    /** whether the list of timezones should be sorted alphabetically or not */
    sorted?: boolean;
}

const CLASSNAME = 'time-zone-picker';

/**
 * Renders a TimeZonePickerField with a given format.
 * @family Pickers
 * @param TimeZonePickerFieldProps
 * @returns TimeZonePickerField
 */
export const TimeZonePickerField: React.FC<TimeZonePickerFieldProps> = ({
    className,
    icon = mdiMapClockOutline,
    label,
    optionProps,
    language,
    sorted,
    timeZoneNameFormat,
    withLanguageFormatting,
    withLocation,
    withTimeZoneName,
    withTimeZoneOffset = true,
    onTimeZoneChanged,
    selectedTimeZone,
    date,
    ...forwardedProps
}) => {
    const { translateKey } = useTranslate();
    const { block, element } = useClassnames(CLASSNAME);

    const options = React.useMemo(
        () =>
            getTimeZonesDetails({
                sorted,
                language,
                timeZoneNameFormat,
                withLanguageFormatting,
                withLocation,
                withTimeZoneName,
                withTimeZoneOffset,
                date,
            }),
        [
            date,
            language,
            sorted,
            timeZoneNameFormat,
            withLanguageFormatting,
            withLocation,
            withTimeZoneName,
            withTimeZoneOffset,
        ],
    );

    const value = React.useMemo(() => {
        if (!selectedTimeZone) {
            return undefined;
        }
        const name = getFormattedTimezone({
            timeZone: selectedTimeZone,
            language,
            timeZoneNameFormat,
            withLanguageFormatting,
            withLocation,
            withTimeZoneName,
            withTimeZoneOffset,
            date,
        });
        return options.find((option) => option.name === name);
    }, [
        date,
        language,
        options,
        selectedTimeZone,
        timeZoneNameFormat,
        withLanguageFormatting,
        withLocation,
        withTimeZoneName,
        withTimeZoneOffset,
    ]);

    return (
        <SelectTextField<TimeZone>
            selectionType="single"
            icon={icon || undefined}
            label={label || translateKey(GLOBAL.TIMEZONE)}
            value={value}
            onChange={onTimeZoneChanged}
            hasClearButton={false}
            autoFilter
            options={options}
            getOptionId="name"
            className={block(undefined, className)}
            listProps={{ className: element('list') }}
            renderOption={() => <SelectTextField.Option {...optionProps} />}
            showEmptyState
            {...forwardedProps}
        />
    );
};
